import React from 'react';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { Page, LayoutSingleColumn, NamedLink } from '../../components';

import TopbarContainer from '../TopbarContainer/TopbarContainer';
import FooterContainer from '../FooterContainer/FooterContainer';

import css from './AboutUsPage.module.css';

const CARE_ITEMS = [
  {
    title: 'Do not cut corners',
    text: 'Every seller and lender is ID-verified before they get paid, and every payment runs through Stripe. No exceptions, no shortcuts.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l7 3v6c0 4.5-3 8-7 9-4-1-7-4.5-7-9V6l7-3z" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
  },
  {
    title: 'Handle with honesty',
    text: 'Listings show real photos and real flaws. If it says "gently worn," we mean gently worn.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 12s4-7 10-7 10 7 10 7-4 7-10 7-10-7-10-7z" />
        <circle cx="12" cy="12" r="3" />
      </svg>
    ),
  },
  {
    title: 'Reuse, don’t discard',
    text: 'Every rental and resale keeps one more piece out of a landfill a little longer.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 2l4 4-4 4" />
        <path d="M3 11V9a4 4 0 0 1 4-4h14" />
        <path d="M7 22l-4-4 4-4" />
        <path d="M21 13v2a4 4 0 0 1-4 4H3" />
      </svg>
    ),
  },
  {
    title: 'Iron out issues, fast',
    text: 'Something go sideways? A real person answers, usually the same day.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.4 8.5 8.5 0 0 1-4-1L3 20l1.1-4.5a8.4 8.4 0 0 1-1-4A8.38 8.38 0 0 1 11.6 3a8.5 8.5 0 0 1 9.4 8.5z" />
      </svg>
    ),
  },
];

const STATS = [
  { num: '80%', text: 'of every dollar goes straight to the person who listed the piece' },
  { num: '100%', text: 'of sellers & lenders ID-verified before payout' },
  { num: 'BC', text: 'born and based' },
  { num: '∞', text: 'outfits from closets that used to just sit there' },
];

export const AboutUsPageComponent = props => {
  const { scrollingDisabled } = props;

  return (
    <Page
      title="About Us | Nothing to Wear"
      description="Nothing to Wear is a Kelowna, BC-based peer-to-peer marketplace for renting, buying, lending, and selling clothing — sustainable fashion, real closets."
      scrollingDisabled={scrollingDisabled}
    >
      <LayoutSingleColumn topbar={<TopbarContainer />} footer={<FooterContainer />}>
        <div className={css.page}>
          <section className={css.hero}>
            <span className={css.eyebrow}>About us</span>
            <h1 className={css.heroTitle}>
              Hi. It&rsquo;s us. <i className={css.heroTitleItalic}>We&rsquo;re the problem.</i>
            </h1>
            <div className={css.metaLine}>
              <span>Kelowna, BC</span>
              <span className={css.dot} aria-hidden="true" />
              <span>Neighbour-to-neighbour</span>
              <span className={css.dot} aria-hidden="true" />
              <span>Powered by real closets</span>
            </div>
          </section>

          <section className={css.story}>
            <div className={css.storyCopy}>
              <span className={css.storyEyebrow}>The scoop</span>
              <p>
                Our favourite pastime: standing in front of a full closet, insisting we have
                nothing to wear.
              </p>
              <p>
                The clothes say otherwise. We have plenty. We just want{' '}
                <i>something else</i>. So, historically, we bought more.
              </p>
              <div className={css.pull}>
                <blockquote className={css.pullQuote}>
                  &ldquo;Nothing to wear&rdquo; was never about having enough. It was about
                  having the right thing, right now.
                </blockquote>
              </div>
              <p>
                But what if someone already had that <i>something else</i>? What if we could
                just borrow it &mdash; for a small fee, for all their trouble &mdash; wear it,
                feel amazing in it, and hand it back before it takes up valuable real estate in
                our closet? The kind of thing we&rsquo;d only wear once or twice anyway.
              </p>
              <p>
                They get paid for a piece that was just taking up space. We get to wear it
                without adding to ours. Win-win.
              </p>
              <p>
                You still get the dopamine hit from adding to cart. It just doesn&rsquo;t end up
                in a landfill six months later.
              </p>
              <p>Bonus: we didn&rsquo;t buy from a warehouse. We borrowed from somebody real.</p>
              <p>That&rsquo;s the idea here.</p>
              <p>
                It&rsquo;s about freedom. It&rsquo;s about access. We&rsquo;re opening (closet)
                doors to give you more options &mdash; on the days you have &ldquo;nothing to
                wear.&rdquo;
              </p>
            </div>
          </section>

          <section className={css.care}>
            <div className={css.carePanel}>
              <span className={css.careTab}>Care instructions</span>
              <div className={css.careHead}>
                <h2 className={css.careHeading}>How to care for us</h2>
                <p className={css.careSubheading}>
                  A few things we hold ourselves to &mdash; no fine print required.
                </p>
              </div>
              <div className={css.careGrid}>
                {CARE_ITEMS.map(item => (
                  <div className={css.careItem} key={item.title}>
                    <div className={css.careIcon}>{item.icon}</div>
                    <h3 className={css.careItemTitle}>{item.title}</h3>
                    <p className={css.careItemText}>{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={css.stats}>
            {STATS.map(stat => (
              <div className={css.stat} key={stat.num}>
                <span className={css.statNum}>{stat.num}</span>
                <p className={css.statText}>{stat.text}</p>
              </div>
            ))}
          </section>

          <section className={css.cta}>
            <h2 className={css.ctaHeading}>
              Still think you have <i className={css.ctaHeadingItalic}>nothing to wear?</i>
            </h2>
            <p className={css.ctaText}>
              Prove yourself wrong. Browse our closets, or add yours to the mix.
            </p>
            <div className={css.ctaButtons}>
              <NamedLink name="SearchPage" className={classNames(css.btn, css.btnSolid)}>
                Browse the closet
              </NamedLink>
              <NamedLink name="HowItWorksPage" className={classNames(css.btn, css.btnGhost)}>
                See how it works
              </NamedLink>
            </div>
          </section>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const AboutUsPage = compose(connect(mapStateToProps))(AboutUsPageComponent);

export default AboutUsPage;
